import { Fragment } from 'react';
import { TextKey, DataTestId, FeatureFlag, RouteName } from '@mews-commander/enums';
import { IconName } from '@optimus-web/core';
import { useCommanderSelector } from '@mews-commander/utils';
import { useFeatureFlag } from '@mews/framework';
import { areCurrentEnterpriseCustomerStatisticsEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    hasReadOnlyPrivilegesForCustomerData,
    isCurrentProfileEnabledToManageCustomers,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getSelectedServiceId } from '@mews-commander/legacy-entities/src/ducks/serviceSelect';
import DashboardTitle from '../components/DashboardTitle';
import DashboardTextLink from '../components/DashboardTextLink';
import { InhouseProfilesStatisticsChart } from './InhouseProfilesStatisticsChart';
import { ArrivingProfilesStatisticsChart } from './ArrivingProfilesStatisticsChart';
import { TotalMatchingGuestProfileContainer } from './TotalMatchingGuestProfileContainer';
import { useGetCustomerStatistics } from './useGetCustomerStatistics';

export const CustomerStatistics = () => {
    const isCustomerManager: boolean = useCommanderSelector(isCurrentProfileEnabledToManageCustomers);
    const customerStatisticsEnabled: boolean = useCommanderSelector(areCurrentEnterpriseCustomerStatisticsEnabled);
    const selectedServiceId: string = useCommanderSelector(getSelectedServiceId);
    const readOnlyCustomer = useCommanderSelector(hasReadOnlyPrivilegesForCustomerData);

    const { data, isPending: isLoading } = useGetCustomerStatistics({
        serviceId: selectedServiceId,
    });

    const newGuestInHouseReportRouteEnabled = useFeatureFlag(FeatureFlag.newGuestInHouseReportRouteEnabled);

    const advancedMatchAndMergeDuplicateGuestProfilesEnabled = useFeatureFlag(
        FeatureFlag.advancedMatchAndMergeDuplicateGuestProfilesEnabled
    );

    return (
        <Fragment>
            <DashboardTitle testId={DataTestId.Customers} textKey={TextKey.CustomerPlural} />
            {(isCustomerManager && customerStatisticsEnabled) || readOnlyCustomer ? (
                <DashboardTextLink
                    textKey={TextKey.CustomerStatisticReport}
                    iconName={IconName.Statistics}
                    route={RouteName.CustomerStatisticReportIndex}
                    routeParams={{
                        query: {
                            'Service.Id': selectedServiceId,
                        },
                    }}
                />
            ) : null}
            <DashboardTextLink
                textKey={TextKey.GuestInHouseReport}
                iconName="home"
                route={
                    newGuestInHouseReportRouteEnabled
                        ? RouteName.GuestsInHouseReportIndex
                        : RouteName.GuestInHouseReportIndex
                }
                routeParams={{
                    query: {
                        'Service.Id': selectedServiceId,
                    },
                }}
            />
            {isCustomerManager || readOnlyCustomer ? (
                <DashboardTextLink
                    textKey={TextKey.CustomerProfileReport}
                    iconName={IconName.CustomerProfiles}
                    route={RouteName.CustomerProfileReportIndex}
                    routeParams={{
                        query: {
                            'Service.Id': selectedServiceId,
                        },
                    }}
                />
            ) : null}
            {advancedMatchAndMergeDuplicateGuestProfilesEnabled && (isCustomerManager || readOnlyCustomer) ? (
                <TotalMatchingGuestProfileContainer />
            ) : null}
            <ArrivingProfilesStatisticsChart
                isAccessible={isCustomerManager}
                dataTestId={DataTestId.ArrivingProfilesStatisticsChart}
                data={data?.arrival}
                isLoading={isLoading}
            />
            <InhouseProfilesStatisticsChart
                isAccessible={isCustomerManager}
                dataTestId={DataTestId.InhouseProfilesStatisticsChart}
                data={data?.inHouse}
                isLoading={isLoading}
            />
        </Fragment>
    );
};
